import { useEffect } from "react";
import { usePersistStore } from "../store";

const FIVE_MINUTES = 5 * 60 * 1000;

/**
 * Requests the browser's geolocation once and stores the result in
 * usePersistStore.currentLocation as [longitude, latitude] (GeoJSON order).
 *
 * Skips the request if a valid (non-zero) location is already in the store.
 * Uses maximumAge of 5 minutes so the browser can return a cached position
 * instead of prompting the user or hitting GPS hardware again.
 *
 * Call this hook once at the app root (App.tsx). Individual components
 * should just read currentLocation from usePersistStore — never call
 * navigator.geolocation directly.
 */
const useGeolocation = () => {
  const { currentLocation, setCurrentLocation } = usePersistStore(
    (s: any) => s
  );

  useEffect(() => {
    if (!navigator.geolocation) return;

    const hasValidLocation =
      currentLocation &&
      currentLocation.length === 2 &&
      (currentLocation[0] !== 0 || currentLocation[1] !== 0);

    if (hasValidLocation) return;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lng = position.coords.longitude;
        const lat = position.coords.latitude;
        setCurrentLocation([lng, lat]);
      },
      (err) => {
        console.warn("Geolocation error:", err.message);
      },
      {
        timeout: 10000,
        maximumAge: FIVE_MINUTES,
        enableHighAccuracy: false,
      }
    );
    // Only run once on mount — don't re-trigger on store changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export default useGeolocation;
